import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { CASE_STUDIES } from "@/data/content";
import { Reveal } from "@/components/bits/Reveal";
import { Sticker, HandNote } from "@/components/bits/Sticker";
import { Thumb } from "@/components/bits/Slate";

const THEMES = {
  dark: { bg: "var(--ink)", fg: "var(--paper)", tab: "var(--blue)", tabFg: "#fff", sub: "rgba(244,241,234,0.6)" },
  yellow: { bg: "var(--yellow)", fg: "var(--ink)", tab: "var(--pink)", tabFg: "var(--ink)", sub: "rgba(22,21,15,0.6)" },
  paper: { bg: "#fff", fg: "var(--ink)", tab: "var(--green)", tabFg: "var(--ink)", sub: "rgba(22,21,15,0.6)" },
  green: { bg: "var(--green)", fg: "var(--ink)", tab: "var(--yellow)", tabFg: "var(--ink)", sub: "rgba(22,21,15,0.65)" },
};

export const CaseStudies = ({ onOpen }) => (
  <section id="work" className="relative max-w-6xl mx-auto px-6 md:px-10 pt-24 md:pt-36 pb-10" data-testid="case-studies-section">
    <Reveal className="flex flex-col md:flex-row md:items-end gap-3 md:gap-8 mb-20">
      <h2 className="pixel-title text-5xl sm:text-6xl md:text-8xl">Featured works</h2>
      <HandNote className="text-2xl md:text-3xl -rotate-2 md:mb-3">explore my world →</HandNote>
    </Reveal>

    <div className="flex flex-col gap-16">
      {CASE_STUDIES.map((c, i) => {
        const t = THEMES[c.theme];
        const mediaLeft = i % 2 === 1 && !c.mediaRight;
        return (
          <motion.article
            key={c.id}
            data-testid={`case-study-${i}`}
            className="relative border-[1.5px] border-[var(--ink)] hard-shadow grid grid-cols-1 md:grid-cols-12 overflow-visible"
            style={{ background: t.bg, color: t.fg }}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="folder-tab" style={{ background: t.tab, color: t.tabFg }}>
              <span className="w-1.5 h-1.5 rounded-full bg-current" /> {c.tab}
            </span>

            <div className={`md:col-span-5 p-8 md:p-12 flex flex-col justify-between ${mediaLeft ? "md:order-2" : ""}`}>
              <div>
                {c.date && <div className="font-mono text-[0.65rem] tracking-[0.2em] uppercase flex items-center gap-2" style={{ color: t.sub }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-current" /> {c.date}
                </div>}
                <h3 className={`${c.date ? "mt-5" : "mt-2"} font-body font-bold text-4xl md:text-5xl tracking-tight leading-[1.02]`}>{c.title}</h3>
                {c.subtitle && <p className="mt-2 font-hand text-2xl md:text-3xl">{c.subtitle}</p>}
                {c.category && <div className="mt-5 font-mono text-[0.65rem] tracking-[0.2em] uppercase" style={{ color: t.sub }}>{c.category}</div>}
                <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: t.sub }}>{c.blurb}</p>
              </div>
              <div className="mt-8">
                {c.tags.length > 0 && <div className="flex flex-wrap gap-2 mb-6">
                  {c.tags.map((tag) => (
                    <span key={tag} className="font-mono text-[0.62rem] uppercase tracking-[0.12em] px-2 py-1 border border-current/40 rounded-sm">{tag}</span>
                  ))}
                </div>}
                <div className="flex items-center gap-4 flex-wrap">
                  <button
                    data-testid={`case-study-view-${i}`}
                    onClick={() => onOpen(c)}
                    className="font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-2 group"
                  >
                    <span className="border-b-[1.5px] border-current pb-0.5 group-hover:pr-2 transition-all">View project</span>
                    <Play size={12} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <HandNote className="text-xl" style={{ color: t.sub }}>role: {c.role}</HandNote>
                </div>
              </div>
            </div>

            <div className={`md:col-span-7 relative p-5 md:p-8 ${mediaLeft ? "md:order-1" : ""}`}>
              <motion.button
                onClick={() => onOpen(c)}
                data-testid={`case-study-thumb-${i}`}
                className={`group relative block w-full border-[1.5px] border-[var(--ink)] overflow-hidden ${c.theme === "green" ? "bg-[var(--green)]" : "bg-black"}`}
                style={{ rotate: i % 2 ? -1.5 : 1.5 }}
                whileHover={{ rotate: 0, scale: 1.01 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
              >
                <Thumb item={c} className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-700" />
                <span className={`absolute ${c.slate ? "left-5 bottom-8" : "inset-0"} flex items-center justify-center`}>
                  <span className="w-16 h-16 rounded-full bg-[var(--yellow)] border-[1.5px] border-[var(--ink)] hard-shadow flex items-center justify-center text-[var(--ink)] group-hover:scale-110 transition-transform">
                    <Play size={20} fill="currentColor" />
                  </span>
                </span>
                {c.duration && <span className="absolute bottom-3 right-3 font-mono text-[0.62rem] bg-white text-[var(--ink)] px-2 py-1 border border-[var(--ink)]">{c.duration}</span>}
              </motion.button>
              <div className="absolute -bottom-4 left-10 md:left-14">
                <Sticker color={["blue", "pink", "green", "purple"][i % 4]} rotate={-4}>{c.role}</Sticker>
              </div>
            </div>
          </motion.article>
        );
      })}
    </div>
  </section>
);
